// TopupApprovalManager (v4.7.5) — verification-gated approval UI.
import React, { useState, useEffect, useCallback } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import { logAdminAction } from '../../lib/adminAuth'
import { isMasterAdminViewSession } from '../../lib/masterAdminAccess'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface TopupUser {
  id: number
  username: string
  email: string
  full_name: string | null
}

interface VerificationBlockchain {
  txHash?: string
  network?: string
  from?: string
  to?: string
  amount?: number
  blockNumber?: number
  confirmations?: number
  requiredConfirmations?: number
  explorerUrl?: string
}

interface VerificationMatches {
  sender?: boolean
  receiver?: boolean
  amount?: boolean
}

interface VerificationResult {
  success: boolean
  status?: 'confirmed' | 'pending_confirmations' | 'failed' | 'override' | string
  errorCode?: string | null
  error?: string | null
  blockchain?: VerificationBlockchain | null
  matches?: VerificationMatches | null
  expectedReceiver?: string | null
  verifiedAt?: string
  verifiedBy?: string
  overridden?: boolean
  reason?: string
}

interface ParsedNotes {
  transactionHash?: string
  senderWallet?: string
  network?: string
  verification?: VerificationResult
}

interface TopupRow {
  id: string
  user_id: number
  topup_payment_amount: string | number
  transaction_reference: string | null
  transaction_notes: string | null
  status: string
  created_at: string
  updated_at: string | null
  user: TopupUser | null
}

interface Props { currentUser?: any }

const parseNotes = (raw: string | null): ParsedNotes => {
  if (!raw) return {}
  try { return JSON.parse(raw) || {} } catch { return {} }
}

const VerificationBadge: React.FC<{ v?: VerificationResult }> = ({ v }) => {
  if (!v) return <span className="inline-block text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300">Not verified</span>
  if (v.overridden) return <span className="inline-block text-xs px-2 py-0.5 rounded bg-orange-900/40 border border-orange-500/40 text-orange-300" title={v.reason || ''}>⚠ Override</span>
  if (v.success) {
    const conf = v.blockchain?.confirmations
    return <span className="inline-block text-xs px-2 py-0.5 rounded bg-green-900/40 border border-green-500/40 text-green-300">✓ Verified{typeof conf === 'number' ? ` (${conf} conf)` : ''}</span>
  }
  if (v.status === 'pending_confirmations') {
    const c = v.blockchain?.confirmations
    const r = v.blockchain?.requiredConfirmations
    return <span className="inline-block text-xs px-2 py-0.5 rounded bg-yellow-900/40 border border-yellow-500/40 text-yellow-300">⏳ {typeof c === 'number' && typeof r === 'number' ? `${c}/${r}` : 'Pending'}</span>
  }
  return <span className="inline-block text-xs px-2 py-0.5 rounded bg-red-900/40 border border-red-500/40 text-red-300" title={v.error || ''}>✗ Failed</span>
}

export const TopupApprovalManager: React.FC<Props> = ({ currentUser }) => {
  const [rows, setRows] = useState<TopupRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending')
  const readOnly = isMasterAdminViewSession()

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'list_pending', data: { status: statusFilter } })
      })
      const j = await r.json()
      if (!r.ok || j?.success === false) throw new Error(j?.error || 'Failed to load top-ups')
      setRows(j?.data?.topups || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load top-ups')
    } finally { setLoading(false) }
  }, [statusFilter])

  useEffect(() => { load() }, [load])

  // Updates a row's transaction_notes in local state with a fresh verification result.
  const patchVerification = (id: string, verification: VerificationResult) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r
      const notes = parseNotes(r.transaction_notes)
      const next = { ...notes, verification }
      return { ...r, transaction_notes: JSON.stringify(next) }
    }))
  }

  const verify = async (row: TopupRow) => {
    if (readOnly || busy.has(row.id)) return
    setBusy(prev => new Set(prev).add(row.id))
    setError(null)
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'verify', data: { topupId: row.id } })
      })
      const j = await r.json()
      if (!r.ok || j?.success === false) throw new Error(j?.error || 'Verification failed')
      const v: VerificationResult | undefined = j?.data?.verification
      if (v) patchVerification(row.id, v)
    } catch (e: any) {
      setError(e.message || 'Verification failed')
    } finally {
      setBusy(prev => { const n = new Set(prev); n.delete(row.id); return n })
    }
  }

  const act = async (op: 'approve' | 'reject', row: TopupRow) => {
    if (readOnly || busy.has(row.id)) return
    const notes = parseNotes(row.transaction_notes)
    const verified = notes.verification?.success === true
    let reason: string | null = null
    let override = false

    if (op === 'reject') {
      reason = window.prompt('Optional reason for rejecting this top-up:', '')
      if (reason === null) return
    } else {
      const amount = parseFloat(String(row.topup_payment_amount || '0'))
      if (!verified) {
        const inconclusive = new Set(['API_ERROR', 'API_TIMEOUT', 'VERIFY_EXCEPTION', 'INSUFFICIENT_CONFIRMATIONS'])
        if (!inconclusive.has(String(notes.verification?.errorCode || ''))) {
          setError('Verify the hash first. A failed hash, wallet, amount, or network cannot be approved.')
          return
        }
        const r = window.prompt(
          `The chain check could not finish for this top-up (explorer delay or not enough confirmations).\n\n` +
          `A written reason can approve $${amount.toFixed(2)} for ${row.user?.username || 'user ' + row.user_id} only in this case.\n\n` +
          `This action will be audited. Enter a reason (min 5 characters) to continue, or Cancel:`,
          ''
        )
        if (r === null) return
        if (r.trim().length < 5) {
          setError('Override reason must be at least 5 characters.')
          return
        }
        reason = r.trim()
        override = true
      } else {
        if (!window.confirm(`Approve top-up of $${amount.toFixed(2)} for ${row.user?.username || 'user ' + row.user_id}? This will credit their commission balance.`)) return
      }
    }
    setBusy(prev => new Set(prev).add(row.id))
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: op,
          data: {
            topupId: row.id,
            reason: reason || undefined,
            ...(op === 'approve' && override ? { override: true } : {})
          }
        })
      })
      const j = await r.json()
      if (!r.ok || j?.success === false) {
        // If approve was rejected because verification failed, surface the verification.
        if (op === 'approve' && j?.verification) patchVerification(row.id, j.verification)
        throw new Error(j?.error || `Failed to ${op} top-up`)
      }
      try {
        await logAdminAction(
          currentUser?.adminUser?.email || currentUser?.email,
          op === 'approve' ? (override ? 'topup_approved_override' : 'topup_approved') : 'topup_rejected',
          'aureus_share_purchases',
          row.id,
          { user_id: row.user_id, topup_payment_amount: row.topup_payment_amount, reason, override }
        )
      } catch (logErr) { console.warn('logAdminAction failed (non-fatal):', logErr) }
      setRows(prev => prev.filter(x => x.id !== row.id))
    } catch (e: any) {
      setError(e.message || `Failed to ${op}`)
    } finally {
      setBusy(prev => { const n = new Set(prev); n.delete(row.id); return n })
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-white">💸 Commission Top-up Approvals</h2>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={load} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-3 py-1 rounded">Refresh</button>
        </div>
      </div>
      {readOnly && <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mb-4 text-yellow-300 text-sm">🔒 Read-only mode — approvals disabled in master admin view.</div>}
      {error && <div className="bg-red-900/20 border border-red-500/30 rounded p-3 mb-4 text-red-300 text-sm">{error}</div>}
      {loading ? (
        <div className="text-gray-300 py-6 text-center">Loading top-up requests…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-400 py-6 text-center">No {statusFilter} top-up requests.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-200">
            <thead className="text-xs uppercase bg-gray-700 text-gray-300">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">TXID</th>
                <th className="px-3 py-2">Sender</th>
                <th className="px-3 py-2">Network</th>
                <th className="px-3 py-2">Verification</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Submitted</th>
                {statusFilter === 'pending' && <th className="px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const notes = parseNotes(r.transaction_notes)
                const amount = parseFloat(String(r.topup_payment_amount || '0'))
                const isBusy = busy.has(r.id)
                const v = notes.verification
                const verified = v?.success === true
                const explorerUrl = v?.blockchain?.explorerUrl
                const approveTitle = verified
                  ? 'Approve and credit balance'
                  : 'On-chain verification has not succeeded — approving will require an override reason'
                return (
                  <tr key={r.id} className="border-b border-gray-700 hover:bg-gray-700/40">
                    <td className="px-3 py-2">
                      <div className="text-white font-medium">{r.user?.username || `user ${r.user_id}`}</div>
                      <div className="text-xs text-gray-400">{r.user?.email}</div>
                    </td>
                    <td className="px-3 py-2 text-yellow-400 font-semibold">${amount.toFixed(2)}</td>
                    <td className="px-3 py-2 break-all max-w-[220px]"><span className="font-mono text-xs">{notes.transactionHash || '—'}</span></td>
                    <td className="px-3 py-2 break-all max-w-[180px]"><span className="font-mono text-xs">{notes.senderWallet || '—'}</span></td>
                    <td className="px-3 py-2">{notes.network || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <VerificationBadge v={v} />
                        {v && !v.success && v.error && (
                          <span className="text-[11px] text-red-300 max-w-[220px] break-words">{v.error}</span>
                        )}
                        {v?.matches && (
                          <span className="text-[11px] text-gray-400">
                            {v.matches.receiver === false && <span className="text-red-300">✗ receiver </span>}
                            {v.matches.amount === false && <span className="text-red-300">✗ amount </span>}
                            {v.matches.sender === false && <span className="text-yellow-300">⚠ sender </span>}
                          </span>
                        )}
                        {explorerUrl && (
                          <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-400 hover:underline">
                            View on explorer
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.transaction_reference || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</td>
                    {statusFilter === 'pending' && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          onClick={() => verify(r)}
                          disabled={readOnly || isBusy || !notes.transactionHash}
                          title={readOnly ? 'Read-only mode' : notes.transactionHash ? 'Run on-chain verification' : 'No transaction hash on this top-up'}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded mr-2"
                        >{isBusy ? '…' : '🔍 Verify'}</button>
                        <button
                          onClick={() => act('approve', r)}
                          disabled={readOnly || isBusy}
                          title={approveTitle}
                          className={`${verified ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded mr-2`}
                        >{isBusy ? '…' : (verified ? '✅ Approve' : '⚠ Approve (override)')}</button>
                        <button
                          onClick={() => act('reject', r)}
                          disabled={readOnly || isBusy}
                          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded"
                        >{isBusy ? '…' : '🚫 Reject'}</button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default TopupApprovalManager
